import React from 'react';
    import { Link } from "react-router-dom";
import routes from 'src/routes';
import url from 'src/util/url';
import Page from 'src/components/Page';
import ExpandDoc from 'src/components/ExpandDoc';
import CodeSamples from 'src/components/CodeSamples';
import SpecLink from 'src/components/SpecLink';

import {stylesDoc, fieldDoc} from 'src/util/iframeFields';

function TokenIframeFieldsPage() {

    return <Page header="Iframe Fields">

        <p>
            The Iframe Fields integration method allows you to integrate with control of the styling of the form whilst keeping the benefits of an iframe solution.
            Each field is rendered as its own iframe, allowing you to customize the placement and styling of the field.
        </p>
        <p>
            Creating a token using the Iframe Fields approach requires that you:
            <ol>
                <li><SpecLink uriFragment="#operation/TokenAuthkey_CreateAuthkey">Create authkey</SpecLink></li>
                <li><SpecLink uriFragment="#operation/TokenAuthkey_AttachTokenDetails">Attach token details</SpecLink></li>
                <li>Using <b>Iframe fields</b>, attach payment details.</li>
                <li><SpecLink uriFragment="#operation/TokenAuthkey_Process">Process token</SpecLink></li>
            </ol> 
        </p>

        <h2>Attaching payment details using Iframe Fields</h2>

        <h3>Html page</h3>
        <h4>Add JavaScript</h4>

        <p>To enable JavaScript integration, you must include the following script tag in your page.</p>
        <CodeSamples
            samples={[
                {
                    language: 'html',
                    content: `<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`
                }]} copyButton />


        <h4>Add payment control</h4>
        <p>Add the Iframe fields to your payment page.  Here is an example:</p>
        <CodeSamples samples={[
            {
                language: 'html',
                content: `
<div id="paymentMethodForm">
    Card number:
    <div class="frame-field" id="cardNumberField"></div>

    Expiry month:
    <div class="frame-field" id="expiryMonthField"></div>

    Expiry year:
    <div class="frame-field" id="expiryYearField"></div>

    Name on card:
    <div class="frame-field" id="nameOnCardField"></div>

    <button id="submitButton">Submit</button>
</div>

<div id="result" style="display: none">
    <p id="outcome" />
</div>

`
            }            
        ]} copyButton />

        <h4>Add styling</h4>
        <p>You may apply your own styling.  Here is an example:</p>

        <CodeSamples samples={[
            {
                language: "css",
                content: `
/* style field div to look like an input field */
.frame-field {
    border: 1px solid #333333;
    background-color: #fefefe;
    height: 1.4em;
    padding: 0.1em;
}
.frame-field.is-focused {
    border-color: rgb(0, 120, 215);
}
.frame-field.is-invalid {
    background-color: #ffcccc;
}
.frame-field.is-valid {
    background-color: #ccffcc;
}
`
            }
        ]} copyButton />

        <h3>JavaScript</h3>
        <p>After obtaining an AuthKey, attaching token details, the next step is to setup the iFrame Fields with <code>BPOINT.token.authkey.setupIframeFields</code>.</p>

        <p>Here is an example:</p>
        <CodeSamples samples={[
        {
                language: 'javascript',
                content: `BPOINT.token.authkey.setupIframeFields(
    authkey,
    {
        card: {
            number: {
                selector: "#cardNumberField",
            },
            expiry: {
                month: {
                    selector: "#expiryMonthField",
                },
                year: {
                    selector: "#expiryYearField",
                },
            },
            name: {
                selector: "#nameOnCardField",
            },
        },
        fieldClasses: {
            focused: 'is-focused',
            invalid: 'is-invalid',
            valid: 'is-valid',
        },
        onFormLoaded: function (controller) {
            document.querySelector("#paymentMethodForm").style.display = "block";
            document.querySelector("#submitButton").addEventListener("click", function () {
                controller.submit(function (code, data) {
                    if (code === "success") {
                        // Add your code here to process your token.  You may choose to process your token later on but you must receive a success status on the callback first, otherwise Process Token will fail.
                    } else {
                        // handle any errors!
                    }
                });
            });
        },
        onFormLoadError: function (errorMsg) {
            document.querySelector("#result").style.display = "block";
            document.querySelector("#outcome").textContent = errorMsg;
        }
    });`
            }
        ]}  copyButton />

        <p>Note that the html tag id must match the selector for the element.  For example, Card number id is <code>cardNumberField</code>, this must match the <code>selector</code> for the <code>card</code> object that is passed into
        <code>BPOINT.token.authkey.setupIframeFields</code>.  If the <code>id</code> and <code>selector</code> don't match the Iframe field will not be displayed.</p>

        <h2>Specification</h2>
        
        <ExpandDoc
            method="BPOINT.token.authkey.setupIframeFields"
            doc={{
                authkey: {
                    type: "string",
                    description: "AuthKey for attaching a token"
                },
                options: {
                    type: "object",
                    description: "Form & field settings",
                    children: {
                        card: {
                            description: "Card fields",
                            type: "object",
                            children: {
                                number: {
                                    description: "Card number field settings",
                                    type: "object",
                                    children: fieldDoc({
                                        label: {
                                            default: 'Card number'
                                        }
                                    })
                                },
                                expiry: {
                                    description: "Expiry fields",
                                    type: "object",
                                    children: {
                                        month: {
                                            description: "Expiry month field settings",
                                            type: "object",
                                            children: fieldDoc({
                                                label: {
                                                    default: 'Expiry month'
                                                }
                                            })
                                        },
                                        year: {
                                            description: "Expiry year field settings",
                                            type: "object",
                                            children: fieldDoc({
                                                label: {
                                                    default: 'Expiry year'
                                                }
                                            })
                                        },
                                    }
                                },
                                name: {
                                    description: "Name on card field settings",
                                    type: "object",
                                    children: fieldDoc({
                                        label: {
                                            default: 'Name on card'
                                        }
                                    })
                                }
                            }
                        },
                        bank: {
                            description: "Bank fields",
                            type: "object",
                            children: {
                                bsb: {
                                    description: "BSB field settings",
                                    type: "object",
                                    children: fieldDoc({
                                        label: {
                                            default: 'BSB'
                                        }
                                    })
                                },
                                account: {
                                    description: "Account number field settings",
                                    type: "object",
                                    children: fieldDoc({
                                        label: {
                                            default: 'Account number'
                                        }
                                    })
                                },
                                name: {
                                    description: "Account name field settings",
                                    type: "object",
                                    children: fieldDoc({
                                        label: {
                                            default: 'Account name'
                                        }
                                    })
                                }
                            }
                        },
                        fieldClasses: {
                            description: "CSS classes that will be automatically added to field elements",
                            type: "object",
                            children: {
                                focused: {
                                    description: "Applied when field is focused",
                                    type: "string"
                                },
                                valid: {
                                    description: "Applied when field value valid",
                                    type: "string"
                                },
                                invalid: {
                                    description: "Applied when field value invalid",
                                    type: "string"
                                }
                            }
                        },
                        styles: {
                            description: <>Field styles (see <a href="#Styling" >Styling</a>)</>,
                            type: "object",
                        },
                        submitButton: {
                            type: "object",
                            description: "Details of the Submit button and its behaviour",
                            children: {
                                selector: {
                                    type: "string",
                                    description: <>Html Id of the Submit button to be disabled.  For example, if your button is called <code>btnSubmit</code>, the value should be <code>#btnSubmit</code></>,
                                },
                                disableOnClick: {
                                    type: "boolean",
                                    description: 'If set to true, the Submit button will be disabled once payment details are accepted'
                                }
                            }
                        },
                        onFormLoaded: {
                            description: "A callback function that is executed when the fields are completely loaded",
                            type: "function",
                            children: {
                                controller: {
                                    type: "object",
                                    description: "Object to communicate with iframe fields",
                                    children: {
                                        focus: {
                                            type: "function",
                                            description: "Focuses iframe field",
                                            children: {
                                                field: {
                                                    type: "string",
                                                    description: "ID of field"
                                                }
                                            }
                                        },
                                        submit: {
                                            type: "function",
                                            description: "Submits payment method",
                                            children: {
                                                callback: {
                                                    type: "function",
                                                    description: "A callback function that is executed when the submission result is received",
                                                    children: {
                                                        code: {
                                                            type: "string",
                                                            description: <>One of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                                                        },
                                                        data: {
                                                            type: "object",
                                                            description: <>See response of <Link to={routes.api.spec + "#operation/TokenAuthkeyClient_AttachPaymentMethod"}>TokenAuthkeyClient - AttachPaymentMethod</Link></>
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        },
                        onFormLoadError: {
                            description: "A callback function that is executed when an error occurs",
                            type: "function",
                            children: {
                                field: {
                                    type: "string",
                                    description: "The details of the error"
                                }
                            }
                        },
                        onFieldFocus: {
                            description: "A callback function that is executed when a field is focused",
                            type: "function",
                            children: {
                                field: {
                                    type: "string",
                                    description: "ID of focused field"
                                }
                            }
                        },
                        onFieldBlur: {
                            description: "A callback function that is executed when a field is blurred (onfocused)",
                            type: "function",
                            children: {
                                field: {
                                    type: "string",
                                    description: "ID of blurred field"
                                }
                            }
                        },
                        onValidate: {
                            description: "A callback function that is executed when a field is modified",
                            type: "function",
                            children: {
                                field: {
                                    type: "string",
                                    description: "ID of validated field"
                                },
                                valid: {
                                    type: "boolean",
                                    description: "Whether the field is valid"
                                }
                            }
                        }
                    }
                }
            }}
        />

        <h2 id="Styling">Styling</h2>
        <p>
            We allow passing styles into Iframe Fields via the <code>options.styles</code> object, or on each individual field when calling <code>BPOINT.token.authkey.setupIframeFields</code>.
            Each style maps directly to a CSS style rule, but only a subset of rules and values are supported.   See example below.
        </p>
        <ExpandDoc
            doc={{
                default: {
                    description: "Default styles",
                    ...stylesDoc
                },
                valid: {
                    description: "Styles applied when the field is valid",
                    ...stylesDoc
                },
                invalid: {
                    description: "Styles applied when the field is invalid",
                    ...stylesDoc
                },
                focus: {
                    description: "Styles applied when the field is focused",
                    ...stylesDoc
                },
                'focus.valid': {
                    description: "Styles applied when the field is valid & focused",
                    ...stylesDoc
                },
                'focus.invalid': {
                    description: "Styles applied when the field is invalid & focused",
                    ...stylesDoc
                },
            }}
        />
        <p>Example of passing styles into <code>BPOINT.token.authkey.setupIframeFields</code>.</p>
        <CodeSamples samples={[
                {
                    language: "javascript",
                    content: `BPOINT.token.authkey.setupIframeFields(
    authkey,
    {
        styles: {
            'default': {
                'font-size': '1rem'
                'font-family': 'Verdana',
                'color': 'black'
            },
            'valid': {
                'color': 'green'
            },
            'invalid': {
                'color': 'red'
            },
            'focus': {
                'font-weight': 'bold'
            }
        },
        card: {
            number: {
                styles: {
                    // override the card number field styles
                    'default': {
                        'color': 'purple'
                    }
                }
            }
        }
        // other fields left off for brevity
    }
`
                }
            ]} copyButton />

        <h2>Updating payment details for a token</h2>
        <p>
            To update the payment details for a token, you will need the pass the Token number into <SpecLink uriFragment="#operation/TokenAuthkey_CreateAuthkey">Create a token authkey</SpecLink>.
        </p>
    </Page>
}

export default TokenIframeFieldsPage;